import React, { useState, useEffect } from "react";
import { fetchFreshFacebookAdsetsByCampaign } from "../../services/getAllCampaigns";
import createDuplicateCampaign from "../../services/createDuplicateCampaign";
import createDuplicateAdset from "../../services/createDuplicateAdset";
import getTimeZoneDeltaUTC from "../../services/adsets/getTimeZone"
import { Modal, Box, Autocomplete, TextField, InputAdornment, Button as MuiButton, FormControl, FormGroup, Switch } from "@mui/material";
import { notify } from "../../utils/notify";

const DuplicateCampaignModal = ({ open, onClose, campaigns = [], loadingCampaigns = false, adsets, refreshCampaigns = null }) => {
  const [advancedSettingsEnabled, setAdvancedSettingsEnabled] = useState(false);
  const [location, setLocation] = useState(null);
  const [audience, setAudience] = useState(null);
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [duplicateAdsetName, setDuplicateAdsetName] = useState("");
  const adAccountId = localStorage.getItem("adAccountId");
  const userId = localStorage.getItem("userId");
  // campaigns and loadingCampaigns are now received as props
  const [selectedCampaignForDuplicate, setSelectedCampaignForDuplicate] = useState(null);
  const [duplicateCampaignName, setDuplicateCampaignName] = useState("");
  const [adSetBudget, setAdSetBudget] = useState(0);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [savedDuplicateData, setSavedDuplicateData] = useState(null);
  const [adSets, setAdSets] = useState([]);
  const [selectedAdSet, setSelectedAdSet] = useState(null);
  const [adsetStatus, setCampaignStatus] = useState(false);
  const [timeZoneDelta, setTimeZoneDelta] = useState(null);


  const resetModalState = () => {
    setSelectedCampaignForDuplicate(null);
    setDuplicateCampaignName("");
    setAdSetBudget(0);
    setSavedDuplicateData(null);
    setAdSets([]);
    setSelectedAdSet(null);
    setIsDuplicating(false);
    setLocation(null);
    setAudience(null);
    setStartDateTime("");
    setEndDateTime("");
    setCampaignStatus(false);
    setAdvancedSettingsEnabled(false);
  };

  const handleDuplicateCampaignSelection = async (event, newValue) => {
    setSelectedCampaignForDuplicate(newValue);
    if (newValue) {
      setDuplicateCampaignName(`${newValue.name} - Copy`);
      if (newValue?.budgetType === "CBO") {
        setAdSetBudget(newValue?.dailyBudget / 100);
      }
      try {
        const adsetsResponse = await fetchFreshFacebookAdsetsByCampaign({
          user_id: userId,
          campaign_id: newValue.campaignId,
          ad_account_id: adAccountId,
          fetch_ads: true
        });
        setAdSets(adsetsResponse?.data || []);
      } catch (error) {
        console.error(error);
        notify("Failed to fetch ad sets", "error");
        setAdSets([]);
      }
    } else {
      resetModalState();
    }
  };

  const handleDuplicateCampaignNameChange = (event) => {
    setDuplicateCampaignName(event.target.value);
  };

  const handleDuplicateAdsetNameChange = (event) => {
    setDuplicateAdsetName(event.target.value);
  };

  const handleAdSetBudgetChange = (event) => {
    const value = event.target.value;
    setAdSetBudget(value === "" ? 0 : Number(value));
  };

  const handleCreateAndContinue = async () => {
    // Step 1 validation (Create Campaign)
    if (!isStep2Active) {
      if (!selectedCampaignForDuplicate || !duplicateCampaignName) {
        notify("Please fill in all required fields", "error");
        return;
      }
      // Only check budget for CBO campaigns in step 1
      if (selectedCampaignForDuplicate?.budgetType === 'CBO' && (!adSetBudget || Number(adSetBudget) <= 0)) {
        notify("Please fill in all required fields", "error");
        return;
      }
    }

    // Step 2 validation (Create Ad Set)
    if (isStep2Active) {
      if (!selectedAdSet) {
        notify("Please fill in all required fields", "error");
        return;
      }
      // Only check budget for non-CBO campaigns in step 2
      if (selectedCampaignForDuplicate?.budgetType !== 'CBO' && (!adSetBudget || Number(adSetBudget) <= 0)) {
        notify("Please fill in all required fields", "error");
        return;
      }
    }

    try {
      setIsDuplicating(true);
      setSavedDuplicateData({
        campaign: selectedCampaignForDuplicate,
        name: duplicateCampaignName,
        budget: adSetBudget,
      });

      // Use saved budget from step 1 if available for CBO campaigns
      const budgetToUse = isStep2Active && savedDuplicateData?.budget ? savedDuplicateData.budget : adSetBudget;

      // Prepare payload for duplicate campaign
      const payload = {
        user_id: userId,
        reference_campaign_id: selectedCampaignForDuplicate.campaignId || selectedCampaignForDuplicate.id,
        new_name: duplicateCampaignName,
        // status: campaignStatus ? "ACTIVE" : "PAUSED",
        ad_account_id: adAccountId,
        lifetime_budget: null,
        apply_targeting_to_copied_adsets: {
          age_min: selectedAdSet?.age_min,
          age_max: selectedAdSet?.age_max
        },
      };
      if (selectedCampaignForDuplicate?.budgetType === 'CBO') {
        payload.daily_budget = Number(budgetToUse) * 100;
      }

      // Log payload and call API when Create Ad Set is clicked
      if (isStep2Active) {
        console.log("Duplicate Campaign Payload:", payload);
        console.log("Budget used:", budgetToUse, "adSetBudget:", adSetBudget, "savedBudget:", savedDuplicateData?.budget);
        try {
          const apiResponse = await createDuplicateCampaign(payload);
          console.log("API Response:", apiResponse);

          if (apiResponse?.success) {
            // notify("Campaign duplicated successfully!", "success");
            if (typeof refreshCampaigns === "function") {
              await refreshCampaigns();
            }
            // Now call duplicate ad set API using copied_campaign_id
            const adAccountId = apiResponse.result.ad_account_id;
            const copiedCampaignId = apiResponse.result.copied_campaign_id;
            const adsetPayload = {
              user_id: selectedAdSet?.userId || userId,
              ad_account_id: adAccountId,
              reference_adset_id: selectedAdSet?.adsetId,
              status: adsetStatus ? "ACTIVE" : "PAUSED",
              campaign_id: copiedCampaignId,
              name: duplicateAdsetName,
              // Advanced settings - format datetime with timezone
              start_time: advancedSettingsEnabled && startDateTime ? (() => {
                const date = new Date(startDateTime);
                const offset = timeZoneDelta !== null ? timeZoneDelta * 60 : date.getTimezoneOffset();
                const sign = offset > 0 ? '+' : '-';
                const hours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0');
                const minutes = (Math.abs(offset) % 60).toString().padStart(2, '0');
                const final = startDateTime + `${sign}${hours}:${minutes}`
                return final;
                // return date.toISOString().replace('Z', `${sign}${hours}:${minutes}`);
                // return date.toISOString();
              })() : null,
              end_time: advancedSettingsEnabled && endDateTime ? (() => {
                const date = new Date(endDateTime);
                const offset = timeZoneDelta !== null ? timeZoneDelta * 60 : date.getTimezoneOffset();
                const sign = offset > 0 ? '+' : '-';
                const hours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0');
                const minutes = (Math.abs(offset) % 60).toString().padStart(2, '0');
                const final = endDateTime + `${sign}${hours}:${minutes}`
                return final;
                // return date.toISOString().replace('Z', `${sign}${hours}:${minutes}`);
                // return date.toISOString();
              })() : null,
              // daily_budget: selectedCampaignForDuplicate?.budgetType !== 'CBO' ? Number(adSetBudget) : undefined,
            };
            if (selectedCampaignForDuplicate?.budgetType !== 'CBO') {
              adsetPayload.daily_budget = Number(adSetBudget) * 100;
            }

            console.log("Duplicate Adset Payload:", adsetPayload);
            try {
              const adsetResponse = await createDuplicateAdset(adsetPayload);
              console.log("Duplicate Adset API Response:", adsetResponse);
              if (adsetResponse?.success) {
                // notify("Ad Set duplicated successfully!", "success");
                // Only close modal and reset state, do not refresh campaigns
                resetModalState();
                if (typeof onClose === "function") {
                  onClose("success", "Campaign and Ad Set duplicated successfully!");
                }
              } else {
                // notify("Failed to duplicate ad set!", "error");
                resetModalState();
                if (typeof onClose === "function") {
                  onClose("error", "Campaign Duplicated but Failed to duplicate ad set!");
                }
              }
            } catch (adsetErr) {
              console.error("Duplicate Adset API Error:", adsetErr);
              resetModalState();
              if (typeof onClose === "function") {
                onClose("error", "Something is wrong!");
              }
            }
          }

          else {
            resetModalState();
            if (typeof onClose === "function") {
              onClose("error", "Failed to duplicate campaign.");
            }
          }
        } catch (err) {
          console.error("API Error:", err);
          resetModalState();
          if (typeof onClose === "function") {
            onClose("error", "Failed to duplicate Campaign/Ad Set!");
          }
        }
      }

      // Fetch fresh ad sets if needed
      if (selectedCampaignForDuplicate?.id) {
        const response = await fetchFreshFacebookAdsetsByCampaign({
          user_id: userId,
          campaign_id: selectedCampaignForDuplicate.id,
          ad_account_id: adAccountId,
          fetch_ads: true
        });
        if (response && Array.isArray(response.data)) setAdSets(response.data);
      }
    } catch (error) {
      console.error(error);
      notify("Failed to fetch ad sets. Please try again.", "error");
    } finally {
      setIsDuplicating(false);
      // Don't reset budget here as it causes the payload to have 0 value
    }
  };

  const isStep2Active = Boolean(savedDuplicateData);

  useEffect(() => {
    const fetchTimeZoneDelta = async () => {
      if (!adAccountId) return;
      const response = await getTimeZoneDeltaUTC(adAccountId);
      if (response) {
        setTimeZoneDelta(response.timezone_offset_hours_utc);
        console.log("TimeZone delta:", response.timezone_offset_hours_utc);
      }

    };
    fetchTimeZoneDelta();
  }, [adAccountId]);

  // Reset modal state whenever modal is closed
  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="campaign-modal-title"
      aria-describedby="campaign-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 613,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 0,
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          border: '1px solid #E5E7EB',
        }}
      >
        <h2
          id="campaign-modal-title"
          className="font-inter font-medium not-italic text-[22px] leading-[1.2] tracking-[-0.01em] mb-3 text-left w-full"
        >
          Duplicate Campaign
        </h2>
        <div
          className="font-inter font-normal not-italic text-[16px] leading-[1.4] text-gray-500 text-left w-full mt-0"
        >
          Create a new campaign by duplicating settings from an existing campaign. After creating the campaign, you&apos;ll
          be able to duplicate ad sets.
        </div>
        <hr style={{
          position: 'absolute',
          left: 0,
          top: 105,
          width: '100%',
          border: 'none',
          marginTop: 20,
          borderBottom: '1px solid #E5E7EB',
          margin: 0,
          zIndex: 1,
        }} />
        <div
          className="w-full flex items-center justify-center mt-[40px] mb-6 gap-4"
        >
          <span className="font-medium text-[16px] text-[#1F2937] font-['Inter',sans-serif]">
            <span
              className={`w-8 h-8 rounded-[16px] inline-flex items-center justify-center ${isStep2Active ? 'bg-[#6E65ED]' : 'bg-[#6E65ED]'
                } text-white opacity-100 mr-2`}
            >
              {isStep2Active ? (
                <img src="/tick.svg" alt="done" className="w-5 h-5" />
              ) : (
                <span className="font-['Inter'] font-normal text-[14px] leading-[20px] tracking-[0%]">
                  1
                </span>
              )}
            </span>
            <span className="font-['Inter'] font-normal text-[14px] leading-[20px] tracking-[0%] text-[#111827]">
              Create Campaign
            </span>
          </span>
          <span className="h-[2px] w-10 bg-[#D1D5DB] mx-2"></span>
          <span className="font-medium text-[16px] text-[#1F2937] font-['Inter',sans-serif]">
            <span
              className={`w-8 h-8 rounded-full inline-flex items-center justify-center mr-2 transition-colors duration-200 ${isStep2Active ? "bg-[#6E65ED] text-white" : "bg-[#F3F4F6] text-[#D1D5DB]"
                }`}
            >
              <span className="font-normal text-[14px] leading-[20px]">
                2
              </span>
            </span>
            <span className="font-normal text-[14px] leading-[20px] text-[#111827]">
              Create Ad Set
            </span>
          </span>
        </div>
        <hr style={{
          width: '100%',
          border: 'none',
          borderBottom: '1px solid #E5E7EB',
          margin: 0,
        }} />
        {!isStep2Active ? (
          <>
            <div className="mt-6 mb-2 font-['Inter',sans-serif] font-medium not-italic text-[14px] leading-[120%] text-[#1F2937] text-left w-full">
              Select campaign to copy settings from
            </div>
            <div style={{ width: 565 }}>
              <Autocomplete
                options={campaigns}
                value={selectedCampaignForDuplicate}
                onChange={handleDuplicateCampaignSelection}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option?.campaignId === value?.campaignId}
                loading={loadingCampaigns}
                noOptionsText={loadingCampaigns ? "Loading..." : "No campaigns found"}
                sx={{
                  width: 565,
                  height: 46,
                  borderRadius: '8px',
                  borderColor: '#D7D7D7',
                  justifyContent: 'space-between',
                  boxShadow: '0px 1px 2px 0px #0000000A',
                  opacity: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    borderColor: '#D7D7D7',
                    minHeight: 46,
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    boxShadow: '0px 1px 2px 0px #0000000A',
                    backgroundColor: '#fff',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D7D7D7',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D7D7D7',
                      borderWidth: '1px'
                    },
                    '&.Mui-focused': {
                      outline: 'none',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D7D7D7',
                  },
                }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: '8px',
                      border: '1px solid #D7D7D7',
                      boxSizing: 'border-box',
                      padding: '8px',
                      borderRadius: '8px',
                      width: 565,
                      overflowX: 'hidden',
                    },
                  },
                }}
                ListboxProps={{
                  style: {
                    padding: 0,
                    overflowX: 'hidden',
                  },
                }}
                renderOption={(props, option, idx, { selected }) => {
                  // Use id, campaignId, or fallback to name+idx for uniqueness
                  const uniqueKey = option.id || option.campaignId || (option.name + '-' + idx);
                  const { key, ...rest } = props;
                  return (
                    <li
                      key={uniqueKey}
                      {...rest}
                      className={`w-full h-[50px] rounded-lg p-3 gap-1 opacity-100 flex items-center box-border
        ${selected ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = selected ? '#F3F4F6' : 'inherit';
                      }}
                    >
                      <span style={{ fontWeight: 500, fontSize: '15px', color: '#1F2937', marginRight: '10px' }}>{option.name || ''}</span>
                      <span
                        style={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '16px',
                          textAlign: 'center',
                          color: option.budgetType === 'CBO' ? '#5B21B6' : '#065F46',
                          background: option.budgetType === 'CBO' ? '#EDE9FE' : '#D1FAE5',
                          borderRadius: '10px',
                          padding: '2px 10px',
                          display: 'inline-block',
                          width: '46px',
                          height: '20px',
                        }}
                      >
                        {option.budgetType || '—'}
                      </span>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <div style={{ position: 'relative', width: '100%' }}>
                    <TextField
                      {...params}
                      placeholder="Select Campaign"
                      variant="outlined"
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start" sx={{ marginRight: 0 }}>
                            <span className="flex items-center pl-3 pr-1">
                              <img src="/campaign.svg" alt="pointer" className="w-5 h-5" />
                            </span>
                          </InputAdornment>
                        ),
                        endAdornment: params.InputProps.endAdornment, // Only keep the dropdown arrow
                        style: { paddingLeft: 0, textAlign: 'left', outline: 'none' },
                      }}
                      inputProps={{
                        ...params.inputProps,
                        style: { ...params.inputProps?.style, paddingLeft: 0, textAlign: 'left', outline: 'none' },
                      }}
                    />
                    {/* Budget type overlay positioned right after the text */}
                    {selectedCampaignForDuplicate && (
                      <span
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: `calc(60px + ${selectedCampaignForDuplicate.name.length * 6.5}px + 8px)`,
                          transform: 'translateY(-50%)',
                          width: '46px',
                          height: '20px',
                          borderRadius: '10px',
                          padding: '2px 10px',
                          background: selectedCampaignForDuplicate.budgetType === 'ABO' ? '#D1FAE5' : '#EDE9FE',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'Inter',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '16px',
                          letterSpacing: '0',
                          textAlign: 'center',
                          color: selectedCampaignForDuplicate.budgetType === 'ABO' ? '#065F46' : '#5B21B6',
                          pointerEvents: 'none',
                          zIndex: 1,
                        }}
                      >
                        {selectedCampaignForDuplicate.budgetType}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          </>
        ) : (
          <>
            <div className="mt-6 mb-2 font-medium text-sm leading-[120%] text-gray-800 text-left w-full">
              Select ad set you want to copy settings from
            </div>
            <div style={{ width: 565 }}>
              {/* Combine campaign ad sets and all ad sets, campaign ad sets first, no duplicates, with a label separator */}
              {(() => {
                const campaignAdSets = adSets || [];
                const allAdSets = adsets || [];
                // Normalize IDs to string for robust comparison
                const getAdSetId = (a) => String(a.id ?? a.adsetId ?? '');
                const campaignAdSetIds = new Set(campaignAdSets.map(getAdSetId));
                const filteredOtherAdSets = allAdSets.filter(a => !campaignAdSetIds.has(getAdSetId(a)));
                const combinedAdSets = [
                  ...campaignAdSets,
                  ...(filteredOtherAdSets.length > 0 ? [{ type: 'separator', label: 'Other Ad Sets' }] : []),
                  ...filteredOtherAdSets
                ];
                return (
                  <Autocomplete
                    options={combinedAdSets}
                    value={selectedAdSet}
                    onChange={(_, value) => {
                      // Prevent selecting the separator
                      if (value && value.type === 'separator') return;
                      setSelectedAdSet(value);
                      setAdSetBudget(0);
                      if (value && value.name) {
                        setDuplicateAdsetName(`${value.name} - Copy`);
                        if (value.dailyBudget) {
                          setAdSetBudget(Number(value.dailyBudget) / 100);
                        }
                      } else {
                        setDuplicateAdsetName("");
                      }
                    }}
                    getOptionLabel={(option) => option.type === 'separator' ? option.label : option.name}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    sx={{
                      width: 565,
                      height: 46,
                      borderRadius: '8px',
                      borderColor: '#D7D7D7',
                      justifyContent: 'space-between',
                      boxShadow: '0px 1px 2px 0px #0000000A',
                      opacity: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        borderColor: '#D7D7D7',
                        minHeight: 46,
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        boxShadow: '0px 1px 2px 0px #0000000A',
                        backgroundColor: '#fff',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#D7D7D7',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#D7D7D7',
                          borderWidth: '1px'
                        },
                        '&.Mui-focused': {
                          outline: 'none',
                        },
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#D7D7D7',
                      },
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: '8px',
                          border: '1px solid #D7D7D7',
                          boxSizing: 'border-box',
                          padding: '8px',
                          borderRadius: '8px',
                          width: 565,
                          overflowX: 'hidden',
                        },
                      },
                    }}
                    ListboxProps={{
                      style: {
                        padding: 0,
                        overflowX: 'hidden',
                      },
                    }}
                    renderOption={(props, option, idx, { selected }) => {
                      if (option.type === 'separator') {
                        return (
                          <li key={option.label + '-' + idx} style={{
                            padding: '8px 0 8px 12px',
                            textAlign: 'left',
                            fontWeight: 600,
                            color: '#6B7280',
                            background: '#F3F4F6',
                            borderRadius: '6px',
                            fontSize: '13px',
                            margin: '4px 0',
                          }}>
                            {option.label}
                          </li>
                        );
                      }
                      // Use id, adsetId, or fallback to name+idx for uniqueness
                      const uniqueKey = option.id || option.adsetId || (option.name + '-' + idx);
                      const { key, ...rest } = props;
                      // Check if this ad set is in filteredOtherAdSets (i.e., 'Other Ad Sets')
                      const isOtherAdSet = filteredOtherAdSets.some(a => String(a.id ?? a.adsetId ?? '') === String(option.id ?? option.adsetId ?? ''));
                      return (
                        <li
                          key={uniqueKey}
                          {...rest}
                          className={`w-full rounded-lg p-3 gap-1 opacity-100 flex items-center box-border 
        ${selected ? 'bg-gray-100' : 'bg-transparent'} 
        hover:bg-gray-100`}
                          style={{ minHeight: 50, height: 'auto', whiteSpace: 'normal', alignItems: 'flex-start', flexDirection: 'column' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F3F4F6';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = selected ? '#F3F4F6' : 'inherit';
                          }}
                        >
                          <span>{option.name || ''}</span>
                          {/* Show campaign name below if in Other Ad Sets and campaignName exists */}
                          {isOtherAdSet && option.campaignName && (
                            <span style={{ fontSize: '12px', color: '#6B7280', marginTop: 2 }}>{option.campaignName}</span>
                          )}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Ad Set"
                        variant="outlined"
                        required
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start" sx={{ marginRight: 0 }}>
                              <span className="flex items-center pl-3 pr-1">
                                <img src="/campaign.svg" alt="adset" className="w-5 h-5" />
                              </span>
                            </InputAdornment>
                          ),
                          style: { paddingLeft: 0, textAlign: 'left', outline: 'none' },
                        }}
                        inputProps={{
                          ...params.inputProps,
                          style: { ...params.inputProps?.style, paddingLeft: 0, textAlign: 'left', outline: 'none' },
                        }}
                      />
                    )}
                  />
                );
              })()}
            </div>
            {selectedAdSet && (
              <>
                <div style={{ marginTop: 24, width: '100%' }}>
                  <div className="font-inter font-medium text-sm leading-[120%] tracking-[0] text-gray-900 mb-2">
                    Name of the new ad set
                  </div>
                  <TextField
                    value={duplicateAdsetName}
                    onChange={handleDuplicateAdsetNameChange}
                    placeholder="Enter ad set name"
                    variant="outlined"
                    fullWidth={false}
                    size="medium"
                    InputProps={{
                      className:
                        "rounded-lg font-inter text-[15px] bg-white w-[565px] h-[46px] gap-[10px] opacity-100 p-3 box-border",
                    }}
                    sx={{
                      marginBottom: 0,
                      width: 565,
                      height: 46,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        height: 46,
                        boxShadow: '0px 1px 2px 0px #0000000A',
                        border: '1px solid #D7D7D7',
                        padding: '0 12px',
                        background: '#fff',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: 0,
                        height: 46,
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  />
                </div>
                <div style={{ marginTop: 24, width: '100%' }}>
                  {/* Show Ad Set Budget only if campaign type is NOT CBO */}
                  {selectedCampaignForDuplicate?.budgetType !== 'CBO' && (
                    <>
                      <div className="font-inter font-medium text-[14px] leading-[120%] tracking-[0%] text-gray-800 mb-2">
                        Ad Set Budget
                      </div>
                      <TextField
                        value={adSetBudget === 0 ? "" : String(adSetBudget)}
                        onChange={handleAdSetBudgetChange}
                        placeholder="0"
                        variant="outlined"
                        fullWidth={false}
                        size="medium"
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ marginRight: 0 }}>
                              <img src="/dollar.svg" alt="$" className="w-5 h-5" />
                            </InputAdornment>
                          ),
                          className: "rounded-lg font-inter text-[15px] bg-white w-[565px] h-[46px] gap-[10px] opacity-100 p-3 box-border",
                          inputProps: { min: 0 },
                        }}
                        sx={{
                          marginBottom: 0,
                          width: 565,
                          height: 46,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            height: 46,
                            boxShadow: '0px 1px 2px 0px #0000000A',
                            border: '1px solid #D7D7D7',
                            padding: '0 12px',
                            background: '#fff',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: 0,
                            height: 46,
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                      />
                    </>
                  )}
                  <hr style={{ width: '100%', border: 'none', borderBottom: '1px solid #E5E7EB', margin: '20px 0' }} />
                  <div className="mt-0 w-[565px] h-[46px] flex items-center justify-between bg-[#F3F4F6] rounded-lg px-4 py-3 gap-2">
                    <span className="flex items-center gap-2">
                      <img src="/settings.svg" alt="settings" className="w-5 h-5" />
                      <span className="font-inter font-medium text-base leading-[140%] tracking-[0%] text-center text-gray-800">
                        Edit Advanced Settings
                      </span>
                    </span>

                    <Switch
                      checked={advancedSettingsEnabled}
                      onChange={(_, checked) => setAdvancedSettingsEnabled(checked)}
                      color="primary"
                      inputProps={{ 'aria-label': 'Edit Advanced Settings' }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#6E65ED',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#6E65ED',
                        },
                      }}
                    />
                  </div>
                  {advancedSettingsEnabled && (
                    <div className="w-full mt-4">
                      {/* Start Date and End Date container */}
                      <div className="mt-6 w-[565px] flex gap-4">
                        <div className="w-[270px] flex flex-col gap-2">
                          <span className="font-inter font-medium text-sm leading-[120%] text-gray-800 mb-1">
                            Start Date & Time
                          </span>
                          <input
                            type="datetime-local"
                            value={startDateTime || ''}
                            onChange={e => {
                              setStartDateTime(e.target.value);
                              // set end date to the next day midnight
                              if (e.target.value) {
                                const nextDay = new Date(e.target.value);
                                nextDay.setDate(nextDay.getDate() + 1);
                                nextDay.setHours(0, 0, 0, 0);

                                // Format the date to match datetime-local input format
                                const year = nextDay.getFullYear();
                                const month = String(nextDay.getMonth() + 1).padStart(2, '0');
                                const day = String(nextDay.getDate()).padStart(2, '0');
                                setEndDateTime(`${year}-${month}-${day}T23:59`);
                              }
                            }}
                            className={`w-full h-[46px] rounded-lg border border-[#E4E4E7] px-3 text-[15px] font-inter bg-white opacity-100 ${!startDateTime ? 'text-gray-500' : 'text-gray-800'
                              }`}
                          />
                        </div>
                        <div className="w-[270px] flex flex-col gap-2">
                          <span className="font-inter font-medium text-sm leading-[120%] tracking-[0%] text-gray-800 mb-1">
                            End Date & Time
                          </span>
                          <input
                            type="datetime-local"
                            value={endDateTime || ''}
                            onChange={e => setEndDateTime(e.target.value)}
                            className={`w-full h-[46px] rounded-lg border border-[#E4E4E7] px-3 text-[15px] font-inter bg-white ${!endDateTime ? 'text-gray-500' : 'text-gray-800'}`}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
        {!isStep2Active && selectedCampaignForDuplicate && (
          <FormControl component="fieldset" style={{ width: '100%' }}>
            <FormGroup>
              <div className="w-full mt-6">
                <div className="font-inter font-medium text-sm leading-[120%] text-gray-800 mb-2">
                  Name of the Campaign
                </div>
                <TextField
                  value={duplicateCampaignName}
                  onChange={handleDuplicateCampaignNameChange}
                  placeholder="Enter campaign name"
                  variant="outlined"
                  fullWidth={false}
                  size="medium"
                  InputProps={{
                    className:
                      "rounded-lg font-inter text-[15px] bg-white w-[565px] h-[46px] gap-[10px] opacity-100 p-3 box-border",
                  }}
                  sx={{
                    marginBottom: 0,
                    width: 565,
                    height: 46,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      height: 46,
                      boxShadow: '0px 1px 2px 0px #0000000A',
                      border: '1px solid #D7D7D7',
                      padding: '0 12px',
                      background: '#fff',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: 0,
                      height: 46,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                />
              </div>
              {duplicateCampaignName && (
                <>
                  {/* Show Campaign Budget only if campaign type is CBO */}
                  {selectedCampaignForDuplicate?.budgetType === 'CBO' && (
                    <div className="w-full mt-6">
                      <div className="font-['Inter'] font-medium text-[14px] leading-[120%] tracking-[0%] text-[#1F2937] mb-2">
                        Campaign Budget
                      </div>
                      <TextField
                        value={adSetBudget === 0 ? "" : String(adSetBudget)}
                        onChange={handleAdSetBudgetChange}
                        placeholder="0"
                        variant="outlined"
                        fullWidth={false}
                        size="medium"
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ marginRight: 0 }}>
                              <img src="/dollar.svg" alt="$" style={{ width: 20, height: 20 }} />
                            </InputAdornment>
                          ),
                          className:
                            "rounded-lg font-['Inter'] text-[15px] bg-white w-[35.3125rem] h-[46px] gap-2.5 opacity-100 p-3 box-border",
                          inputProps: { min: 0 },
                        }}
                        sx={{
                          marginBottom: 0,
                          width: 565,
                          height: 46,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            height: 46,
                            boxShadow: '0px 1px 2px 0px #0000000A',
                            border: '1px solid #D7D7D7',
                            padding: '0 12px',
                            background: '#fff',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: 0,
                            height: 46,
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </FormGroup>
          </FormControl>
        )}
        {/* Paused/Active Switch will be rendered next to buttons in step 2 */}
        <div className="flex items-center justify-end w-full gap-4 mt-6">
          {isStep2Active && (
            <div className="flex items-center gap-2 mr-auto">
              <span className="mr-2 font-medium text-[15px]">Paused</span>
              <Switch
                checked={adsetStatus}
                onChange={e => setCampaignStatus(e.target.checked)}
                color="primary"
                inputProps={{ 'aria-label': 'campaign status switch' }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#6E65ED',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#6E65ED',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#D1C4E9',
                  },
                }}
              />
              <span className="text-[14px] text-gray-500 ml-1.5">Active</span>
            </div>
          )}
          <MuiButton
            onClick={onClose}
            className="mr-2"
            disabled={isDuplicating}
            style={{
              width: 83,
              height: 38,
              borderRadius: 8,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: '#00000026',
              gap: 10,
              opacity: 1,
              paddingTop: 8,
              paddingRight: 16,
              paddingBottom: 8,
              paddingLeft: 16,
              boxSizing: 'border-box',
              minWidth: 0,
              background: '#fff',
              color: '#1F2937',
              fontWeight: 500,
              fontSize: 16,
              lineHeight: '22px',
              textTransform: 'none',
              letterSpacing: 0,
              outline: 'none',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: 16,
                lineHeight: '140%',
                letterSpacing: '-0.03em',
                textAlign: 'center',
                display: 'inline',
              }}
            >
              Cancel
            </span>
          </MuiButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={handleCreateAndContinue}
            disabled={
              isDuplicating ||
              (!isStep2Active && (
                !selectedCampaignForDuplicate ||
                !duplicateCampaignName ||
                (selectedCampaignForDuplicate?.budgetType === 'CBO' && (!adSetBudget || Number(adSetBudget) <= 0))
              )) ||
              (isStep2Active && (
                !selectedAdSet ||
                (selectedCampaignForDuplicate?.budgetType !== 'CBO' && (!adSetBudget || Number(adSetBudget) <= 0))
              ))
            }
            style={{
              height: 38,
              borderRadius: 8,
              gap: 10,
              opacity: 1,
              paddingTop: 8,
              paddingRight: 16,
              paddingBottom: 8,
              paddingLeft: 16,
              background: (
                isDuplicating ||
                (!isStep2Active && (
                  !selectedCampaignForDuplicate ||
                  !duplicateCampaignName ||
                  (selectedCampaignForDuplicate?.budgetType === 'CBO' && (!adSetBudget || Number(adSetBudget) <= 0))
                )) ||
                (isStep2Active && (
                  !selectedAdSet ||
                  (selectedCampaignForDuplicate?.budgetType !== 'CBO' && (!adSetBudget || Number(adSetBudget) <= 0))
                ))
              ) ? '#DDD6FE' : '#6E65ED',
              fontWeight: 500,
              fontSize: 16,
              lineHeight: '22px',
              textTransform: 'none',
              letterSpacing: 0,
              outline: 'none',
              boxShadow: 'none',
              color: '#FFFFFF',
              transition: 'background 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: 16,
                lineHeight: '140%',
                letterSpacing: '-0.03em',
                textAlign: 'center',
                color: '#FFFFFF',
                display: 'inline-block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {isDuplicating
                ? "Duplicating..."
                : isStep2Active
                  ? "Create Ad Set"
                  : "Create and Continue"}
            </span>
          </MuiButton>
        </div>
      </Box>
    </Modal>
  );
};

export default DuplicateCampaignModal;
